import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useWallet } from "@/context/WalletContext";
import { payOnChain } from "@/lib/wallet";
import { timeAgo } from "@/lib/meta";
import { Check, Loader2, Coins, Wallet, Zap } from "lucide-react";

const short = (h) => (!h ? "" : h.length > 14 ? `${h.slice(0, 8)}…${h.slice(-4)}` : h);

export default function Billing() {
  const { user, walletType, refreshUser } = useWallet();
  const [packs, setPacks] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [minTopup, setMinTopup] = useState(5);
  const [ratesSource, setRatesSource] = useState("fallback");
  const [onchain, setOnchain] = useState(false);
  const [currency, setCurrency] = useState("USDC");
  const [tx, setTx] = useState([]);
  const [escrow, setEscrow] = useState(null);
  const [busy, setBusy] = useState(null);
  const [custom, setCustom] = useState("");

  const load = () => {
    api.get("/billing/packs").then(({ data }) => setPacks(data)).catch(() => {});
    api.get("/billing/transactions").then(({ data }) => setTx(data)).catch(() => {});
    api.get("/billing/currencies").then(({ data }) => setCurrencies(data.currencies || [])).catch(() => {});
  };
  const loadCurrencies = () => {
    api.get("/billing/currencies").then(({ data }) => {
      setCurrencies(data.currencies || []);
      setMinTopup(data.min_topup_usd || 5);
      setRatesSource(data.rates_source || "fallback");
      setOnchain(!!data.onchain_enabled);
    }).catch(() => {});
  };
  useEffect(() => { loadCurrencies(); load(); }, []);

  // Real wallets: prompt to sign a refill when balance drops below the threshold (never auto-move funds).
  useEffect(() => {
    if (!user || walletType === "demo") return;
    const ar = user.auto_refill;
    if (!ar?.enabled || (user.juice_balance ?? 0) >= (ar.threshold ?? 0)) return;
    toast("Balance is low — approve a refill?", {
      id: "auto-refill-prompt",
      description: `Add $${ar.amount} in ${ar.currency} to keep campaigns running.`,
      duration: 12000,
      action: {
        label: "Approve refill",
        onClick: async () => {
          try {
            const data = await payOnChain({ amount: ar.amount, currency: ar.currency });
            toast.success(`Refilled $${data.added} in ${data.currency}`);
            await refreshUser();
            load();
          } catch (e) {
            toast.error(e?.response?.data?.detail || e?.message || "Refill failed.");
          }
        },
      },
    });
  }, [user, walletType]); // eslint-disable-line react-hooks/exhaustive-deps

  const cur = currencies.find((c) => c.id === currency);
  const crypto = (usd) => (cur ? (usd / cur.usd).toFixed(cur.decimals) : usd);
  const canPayOnChain = !!cur?.onchain && (
    (cur.network === "solana" && walletType === "phantom") ||
    (cur.network === "evm" && walletType === "evm")
  );

  const pay = async (payload, key) => {
    setBusy(key);
    try {
      let data;
      if (canPayOnChain) {
        data = await payOnChain({ ...payload, currency });
        toast.success(`Paid ${data.crypto_amount} ${data.currency} on-chain → +$${data.added}`, {
          description: data.explorer_url ? "Settled on Solana mainnet" : undefined,
          action: data.explorer_url ? { label: "Solscan", onClick: () => window.open(data.explorer_url, "_blank") } : undefined,
        });
      } else {
        const res = await api.post("/billing/checkout", { ...payload, currency });
        data = res.data;
        toast.success(`Paid ${data.crypto_amount} ${data.currency} → +$${data.added} fuel`, {
          description: `Tx ${short(data.tx_hash)} · SIMULATED`,
        });
      }
      await refreshUser();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Checkout failed.");
    } finally {
      setBusy(null);
    }
  };

  const buyPack = (id) => pay({ pack: id }, id);
  const payCustom = () => {
    const amt = parseFloat(custom);
    if (!amt || amt < minTopup) return toast.error(`Minimum top-up is $${minTopup}`);
    pay({ amount: amt }, "custom").then(() => setCustom(""));
  };

  return (
    <div className="mx-auto max-w-5xl px-5 py-8 lg:px-8">
      <div className="mb-1 flex items-center gap-2"><Coins size={20} className="text-juice" /><h1 className="text-2xl font-bold tracking-tight">Billing</h1></div>
      <p className="mb-6 text-sm text-dust">Pay-as-you-go in crypto. Prepaid juice: <span className="font-mono text-pulp">${user?.juice_balance ?? 0}</span></p>

      {/* currency selector */}
      <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="currency-selector">
        <span className="mr-1 text-xs uppercase tracking-wide text-dust/70">Pay with</span>
        {currencies.map((c) => (
          <button
            key={c.id}
            onClick={() => setCurrency(c.id)}
            data-testid={`currency-${c.id}`}
            className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
              currency === c.id ? "border-juice bg-juice/10 text-juice" : "border-line bg-surface text-dust hover:text-cream"
            }`}
          >
            {c.id}
          </button>
        ))}
        {cur && (
          <span className="ml-1 inline-flex items-center gap-1 font-mono text-[11px] text-dust/60" data-testid="fx-indicator">
            {ratesSource === "live" && <span className="h-1.5 w-1.5 rounded-full bg-pulp animate-soft-pulse" />}
            1 {cur.id} ≈ ${cur.usd} {ratesSource === "live" ? "· live" : ""}
          </span>
        )}
        {canPayOnChain && (
          <span className="ml-auto inline-flex items-center gap-1 rounded-full border border-pulp/40 bg-pulp/10 px-2.5 py-1 text-[11px] font-semibold text-pulp" data-testid="onchain-badge">
            <Zap size={11} /> On-chain live
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {packs.map((p) => {
          const featured = p.id === "week";
          return (
            <div key={p.id} className={`rounded-2xl border p-6 ${featured ? "border-juice bg-juice/5" : "border-line bg-surface"}`} data-testid={`billing-pack-${p.id}`}>
              <div className="mb-2 flex items-center gap-2">
                {featured && <span className="inline-block rounded-full bg-juice px-2.5 py-0.5 text-[11px] font-semibold text-ink">Popular</span>}
                {p.save_pct && <span className="inline-block rounded-full border border-pulp/40 bg-pulp/10 px-2.5 py-0.5 text-[11px] font-semibold text-pulp" data-testid={`pack-save-${p.id}`}>Save {p.save_pct}%</span>}
              </div>
              <h3 className="text-lg font-medium">{p.name}</h3>
              <p className="mt-1 font-mono text-3xl font-semibold text-juice">${p.price}</p>
              <p className="font-mono text-xs text-dust" data-testid={`pack-crypto-${p.id}`}>≈ {crypto(p.price)} {currency}</p>
              <p className="mt-1 text-xs text-dust">{p.duration} · {p.posts} AI posts</p>
              <ul className="mt-4 space-y-2 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-dust"><Check size={14} className="mt-0.5 shrink-0 text-pulp" /> {f}</li>
                ))}
              </ul>
              <Button onClick={() => buyPack(p.id)} disabled={!!busy} className={`mt-5 w-full gap-2 font-semibold ${featured ? "bg-juice text-ink hover:bg-juice/90" : "bg-raised text-cream hover:bg-line/40"}`} data-testid={`buy-pack-${p.id}`}>
                {busy === p.id ? <Loader2 size={16} className="animate-spin" /> : <Wallet size={16} />} Pay {crypto(p.price)} {currency}
              </Button>
            </div>
          );
        })}
      </div>

      {/* pay-as-you-go */}
      <div className="mt-4 rounded-2xl border border-line bg-surface p-6" data-testid="custom-topup">
        <h3 className="text-lg font-medium">Custom top-up</h3>
        <p className="mb-4 text-xs text-dust">Add any amount of prepaid fuel. Minimum ${minTopup}.</p>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative sm:w-48">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-dust">$</span>
            <Input
              type="number" min={minTopup} step="1" value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="100" data-testid="custom-amount-input"
              className="pl-7 font-mono"
            />
          </div>
          {custom && parseFloat(custom) > 0 && (
            <span className="font-mono text-sm text-dust" data-testid="custom-crypto-preview">≈ {crypto(parseFloat(custom))} {currency}</span>
          )}
          <Button onClick={payCustom} disabled={!!busy} className="gap-2 bg-juice font-semibold text-ink hover:bg-juice/90 sm:ml-auto" data-testid="pay-custom-btn">
            {busy === "custom" ? <Loader2 size={16} className="animate-spin" /> : <Wallet size={16} />} Pay with {currency}
          </Button>
        </div>
      </div>

      <div className="mt-8 rounded-2xl border border-line bg-surface" data-testid="billing-history">
        <div className="border-b border-line px-5 py-3"><h2 className="text-sm font-semibold">History</h2></div>
        {tx.map((t) => (
          <div key={t.id} className="flex items-center justify-between border-b border-line/60 px-5 py-3 text-sm last:border-0">
            <div>
              <span className="capitalize text-cream">{t.kind === "refill" ? (t.pack === "custom" ? "Top-up" : `${t.pack} pack`) : `${t.pack} pack`}</span>
              <p className="font-mono text-[11px] text-dust">{timeAgo(t.created_at)}{t.tx_hash ? ` · ${short(t.tx_hash)}` : ""}{t.simulated === false ? " · on-chain" : ""}</p>
            </div>
            <div className="text-right">
              <span className="font-mono text-cream">${t.amount}</span>
              {t.currency && <p className="font-mono text-[11px] text-pulp">{t.crypto_amount} {t.currency}</p>}
              <Link to={`/app/receipt/${t.id}`} className="text-[11px] text-juice hover:underline" data-testid={`receipt-link-${t.id}`}>Receipt →</Link>
            </div>
          </div>
        ))}
        {!tx.length && <p className="p-5 text-sm text-dust">No transactions yet.</p>}
      </div>

      <p className="mt-4 text-xs text-dust/70">
        {onchain
          ? "On-chain payments settle on Solana mainnet to the Wen Moon treasury."
          : "Crypto payments are simulated in this preview — no real transfer."} Pass-through pad and gas fees are billed at cost. Payments are upfront and non-refundable once agents begin.
      </p>
    </div>
  );
}
